'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Music, Search, Loader2, Play, Pause, X, Check } from 'lucide-react';

interface SongResult {
    trackId: number;
    trackName: string;
    artistName: string;
    artworkUrl100: string;
    previewUrl: string;
}

interface SelectedSong {
    name: string;
    previewUrl: string;
    artwork?: string;
}

interface SongPickerProps {
    defaultTrackName?: string;
    defaultPreviewUrl?: string;
    defaultStart?: number;
    defaultEnd?: number;
}

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
const fmt = (s: number) => {
    const sec = Math.max(0, Math.round(s));
    return `0:${sec.toString().padStart(2, '0')}`;
};

/**
 * Buscador de canciones (iTunes) + selector de tramo estilo Instagram: una barra
 * de la canción con una ventana arrastrable que puedes mover/redimensionar y
 * escuchar en directo para saber exactamente qué sonará.
 *
 * Compatible con FormData: inputs ocultos `spotifyTrackName`, `audioPreviewUrl`,
 * `spotifyStartTime`, `spotifyEndTime`.
 */
export function SongPicker({ defaultTrackName, defaultPreviewUrl, defaultStart, defaultEnd }: SongPickerProps) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<SongResult[]>([]);
    const [searching, setSearching] = useState(false);
    const [searched, setSearched] = useState(false);

    const [selected, setSelected] = useState<SelectedSong | null>(
        defaultTrackName ? { name: defaultTrackName, previewUrl: defaultPreviewUrl || '' } : null
    );
    const [duration, setDuration] = useState(30);
    const [start, setStart] = useState<number>(defaultStart ?? 0);
    const [end, setEnd] = useState<number>(defaultEnd ?? 15);

    const audioRef = useRef<HTMLAudioElement>(null);
    const [playing, setPlaying] = useState(false);
    const [playhead, setPlayhead] = useState(0);
    const [previewing, setPreviewing] = useState<string | null>(null);

    // Drag state (refs para usar valores frescos en los listeners de window)
    const trackRef = useRef<HTMLDivElement>(null);
    const [dragging, setDragging] = useState<null | 'start' | 'end' | 'move'>(null);
    const draggingRef = useRef(dragging); draggingRef.current = dragging;
    const startRef = useRef(start); startRef.current = start;
    const endRef = useRef(end); endRef.current = end;
    const durRef = useRef(duration); durRef.current = duration;
    const moveOffsetRef = useRef(0);
    const playingRef = useRef(playing); playingRef.current = playing;

    const bars = useMemo(
        () => Array.from({ length: 56 }, (_, i) => 0.25 + (Math.sin(i * 1.7) * 0.5 + 0.5) * 0.7 * (0.6 + Math.random() * 0.4)),
        [selected?.previewUrl]
    );

    const runSearch = async () => {
        const term = query.trim();
        if (!term) return;
        setSearching(true);
        setSearched(true);
        try {
            const url = `https://itunes.apple.com/search?term=${encodeURIComponent(term)}&media=music&entity=song&limit=8`;
            const r = await fetch(url);
            const data = await r.json();
            setResults((data?.results || []).filter((s: SongResult) => s.previewUrl));
        } catch {
            setResults([]);
        }
        setSearching(false);
    };

    const onSearchKey = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') { e.preventDefault(); runSearch(); }
    };

    const stopAll = () => {
        if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
        setPlaying(false);
        setPreviewing(null);
    };

    const chooseSong = (s: SongResult) => {
        stopAll();
        setSelected({ name: `${s.trackName} — ${s.artistName}`, previewUrl: s.previewUrl, artwork: s.artworkUrl100 });
        setStart(0);
        setEnd(15);
        setPlayhead(0);
        setResults([]);
        setSearched(false);
        setQuery('');
    };

    const toggleResultPreview = (s: SongResult) => {
        const audio = audioRef.current;
        if (!audio) return;
        if (previewing === s.previewUrl && !audio.paused) {
            audio.pause(); setPreviewing(null); return;
        }
        audio.src = s.previewUrl;
        audio.currentTime = 0;
        audio.play().then(() => setPreviewing(s.previewUrl)).catch(() => {});
        setPlaying(false);
    };

    // ---- Selector de tramo ----
    const timeFromClientX = (clientX: number) => {
        const rect = trackRef.current?.getBoundingClientRect();
        if (!rect) return 0;
        return clamp((clientX - rect.left) / rect.width, 0, 1) * durRef.current;
    };

    const ensureAudio = () => {
        const audio = audioRef.current;
        if (audio && selected?.previewUrl && audio.src !== selected.previewUrl) audio.src = selected.previewUrl;
        return audio;
    };

    const seek = (t: number) => {
        const audio = ensureAudio();
        if (!audio) return;
        audio.currentTime = clamp(t, 0, durRef.current);
        setPlayhead(audio.currentTime);
        if (audio.paused) { audio.play().then(() => setPlaying(true)).catch(() => {}); }
    };

    const beginDrag = (mode: 'start' | 'end' | 'move', clientX: number, centered = false) => {
        if (!selected?.previewUrl) return;
        setDragging(mode);
        const t = timeFromClientX(clientX);
        if (mode === 'move') moveOffsetRef.current = centered ? (endRef.current - startRef.current) / 2 : (t - startRef.current);
        const seekTo = mode === 'end' ? Math.max(startRef.current, endRef.current - 1) : (mode === 'move' ? clamp(t - moveOffsetRef.current, 0, durRef.current) : t);
        seek(seekTo);
    };

    const onDragMove = (clientX: number) => {
        const mode = draggingRef.current;
        if (!mode) return;
        const t = timeFromClientX(clientX);
        const dur = durRef.current;
        if (mode === 'start') {
            const ns = clamp(t, 0, endRef.current - 1);
            setStart(ns); seek(ns);
        } else if (mode === 'end') {
            const ne = clamp(t, startRef.current + 1, dur);
            setEnd(ne); seek(Math.max(startRef.current, ne - 1));
        } else {
            const w = endRef.current - startRef.current;
            const ns = clamp(t - moveOffsetRef.current, 0, dur - w);
            setStart(ns); setEnd(ns + w); seek(ns);
        }
    };

    const endDrag = () => {
        setDragging(null);
        const audio = audioRef.current;
        if (audio) { audio.pause(); }
        setPlaying(false);
    };

    useEffect(() => {
        if (!dragging) return;
        const move = (e: PointerEvent) => onDragMove(e.clientX);
        const up = () => endDrag();
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
        return () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dragging]);

    const togglePlaySelection = () => {
        const audio = ensureAudio();
        if (!audio || !selected?.previewUrl) return;
        if (playing) { audio.pause(); setPlaying(false); return; }
        audio.currentTime = start;
        audio.play().then(() => setPlaying(true)).catch(() => {});
    };

    const handleTimeUpdate = () => {
        const audio = audioRef.current;
        if (!audio) return;
        setPlayhead(audio.currentTime);
        // Loop dentro de la ventana al auditar (no mientras se arrastra).
        if (!draggingRef.current && playingRef.current && audio.currentTime >= endRef.current) {
            audio.currentTime = startRef.current;
        }
    };

    const onLoadedMetadata = () => {
        const audio = audioRef.current;
        if (!audio || !isFinite(audio.duration) || audio.duration <= 0) return;
        const d = audio.duration;
        setDuration(d);
        setEnd(prev => clamp(prev || 15, 1, d));
        setStart(prev => clamp(prev, 0, d - 1));
    };

    useEffect(() => () => stopAll(), []);

    const pct = (t: number) => `${(t / duration) * 100}%`;
    const hasRange = !!selected?.previewUrl;

    return (
        <div className="space-y-3 border p-4 rounded-md bg-black/10 border-white/10">
            <Label className="flex items-center gap-2"><Music className="h-4 w-4" /> Canción (opcional)</Label>

            <input type="hidden" name="spotifyTrackName" value={selected?.name || ''} />
            <input type="hidden" name="audioPreviewUrl" value={selected?.previewUrl || ''} />
            <input type="hidden" name="spotifyStartTime" value={selected ? String(Math.round(start)) : ''} />
            <input type="hidden" name="spotifyEndTime" value={selected && end > 0 ? String(Math.round(end)) : ''} />

            <audio ref={audioRef} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={onLoadedMetadata} onEnded={() => { setPlaying(false); setPreviewing(null); }} preload="none" />

            {!selected ? (
                <>
                    <div className="flex gap-2">
                        <div className="relative flex-grow">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={onSearchKey} placeholder="Busca una canción o marcha..." className="pl-9" />
                        </div>
                        <Button type="button" onClick={runSearch} disabled={searching || !query.trim()}>
                            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Buscar'}
                        </Button>
                    </div>

                    {results.length > 0 && (
                        <div className="max-h-64 overflow-y-auto space-y-1 rounded-lg border border-white/10 bg-black/20 p-1">
                            {results.map(s => (
                                <div key={s.trackId} className="flex items-center gap-2 p-2 rounded-md hover:bg-white/5 transition-colors">
                                    <button type="button" onClick={() => toggleResultPreview(s)} className="relative flex-shrink-0 group">
                                        <img src={s.artworkUrl100} alt="" className="h-11 w-11 rounded-md object-cover" />
                                        <span className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-md opacity-0 group-hover:opacity-100 transition-opacity">
                                            {previewing === s.previewUrl ? <Pause className="h-4 w-4 text-white fill-current" /> : <Play className="h-4 w-4 text-white fill-current" />}
                                        </span>
                                    </button>
                                    <div className="flex-grow min-w-0">
                                        <p className="text-sm font-semibold truncate">{s.trackName}</p>
                                        <p className="text-xs text-muted-foreground truncate">{s.artistName}</p>
                                    </div>
                                    <Button type="button" size="sm" variant="secondary" className="flex-shrink-0 h-8" onClick={() => chooseSong(s)}>
                                        <Check className="h-4 w-4 mr-1" /> Elegir
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                    {searched && !searching && results.length === 0 && (
                        <p className="text-xs text-muted-foreground text-center py-2">No se encontraron canciones. Prueba con otro término.</p>
                    )}
                </>
            ) : (
                <div className="space-y-3">
                    <div className="flex items-center gap-3 p-2 rounded-lg bg-primary/10 border border-primary/20">
                        {selected.artwork && <img src={selected.artwork} alt="" className="h-12 w-12 rounded-md object-cover flex-shrink-0" />}
                        <div className="flex-grow min-w-0">
                            <p className="text-sm font-semibold truncate">{selected.name}</p>
                            <p className="text-xs text-muted-foreground">{hasRange ? 'Arrastra el tramo para elegir qué suena' : 'Canción guardada (busca de nuevo para fijar la preview exacta)'}</p>
                        </div>
                        <Button type="button" size="icon" variant="ghost" className="h-8 w-8 flex-shrink-0 text-destructive" onClick={() => { stopAll(); setSelected(null); }} title="Quitar">
                            <X className="h-4 w-4" />
                        </Button>
                    </div>

                    {hasRange && (
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <Button type="button" size="icon" variant="secondary" className="h-9 w-9 flex-shrink-0 rounded-full" onClick={togglePlaySelection}>
                                    {playing ? <Pause className="h-4 w-4 fill-current" /> : <Play className="h-4 w-4 fill-current" />}
                                </Button>

                                {/* Barra / waveform con ventana arrastrable */}
                                <div
                                    ref={trackRef}
                                    className="relative h-12 flex-grow rounded-lg bg-black/40 overflow-hidden select-none"
                                    style={{ touchAction: 'none' }}
                                    onPointerDown={(e) => { if (e.target === e.currentTarget || (e.target as HTMLElement).dataset.bars !== undefined) beginDrag('move', e.clientX, true); }}
                                >
                                    {/* Waveform decorativa */}
                                    <div data-bars className="absolute inset-0 flex items-center gap-[2px] px-1 pointer-events-none">
                                        {bars.map((h, i) => (
                                            <div key={i} className="flex-1 rounded-full bg-white/15" style={{ height: `${h * 100}%` }} />
                                        ))}
                                    </div>

                                    {/* Ventana seleccionada */}
                                    <div
                                        className="absolute top-0 bottom-0 bg-primary/25 border-x-2 border-primary cursor-grab active:cursor-grabbing"
                                        style={{ left: pct(start), width: pct(Math.max(0, end - start)) }}
                                        onPointerDown={(e) => { e.stopPropagation(); beginDrag('move', e.clientX); }}
                                    >
                                        {/* Handle inicio */}
                                        <div
                                            className="absolute -left-1.5 top-0 bottom-0 w-3 flex items-center justify-center cursor-ew-resize"
                                            onPointerDown={(e) => { e.stopPropagation(); beginDrag('start', e.clientX); }}
                                        >
                                            <div className="h-7 w-1.5 rounded-full bg-primary shadow" />
                                        </div>
                                        {/* Handle fin */}
                                        <div
                                            className="absolute -right-1.5 top-0 bottom-0 w-3 flex items-center justify-center cursor-ew-resize"
                                            onPointerDown={(e) => { e.stopPropagation(); beginDrag('end', e.clientX); }}
                                        >
                                            <div className="h-7 w-1.5 rounded-full bg-primary shadow" />
                                        </div>
                                    </div>

                                    {/* Cabezal de reproducción */}
                                    {(playing || dragging) && (
                                        <div className="absolute top-0 bottom-0 w-0.5 bg-white pointer-events-none" style={{ left: pct(playhead) }} />
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center justify-between text-[0.7rem] text-muted-foreground tabular-nums">
                                <span>Desde {fmt(start)}</span>
                                <span>{Math.round(end - start)}s seleccionados</span>
                                <span>Hasta {fmt(end)}</span>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
